import { User, WebStorageStateStore } from 'oidc-client-ts'
import { AuthProvider } from 'react-oidc-context'
import { Loader } from '@tom-ui/ui'
import { FC, ReactNode } from 'react'
import { useAppConfiguration } from './AppConfigurationProvider'

// After the redirect callback: drop the code/state query params AND restore the
// deep link the user originally requested (stored as state.returnTo by
// loginWithRedirect). Without this, redirect_uri (origin) wins and every login
// lands on "/", losing deep links like /tenants. Runs before React Router
// mounts, so BrowserRouter picks up the restored path.
const onSigninCallback = (user?: User | void) => {
  const state = user?.state as { returnTo?: string } | undefined
  const target =
    typeof state?.returnTo === 'string' && state.returnTo.startsWith('/')
      ? state.returnTo
      : window.location.pathname
  window.history.replaceState({}, document.title, target)
}

export const AuthProviderWithHistory: FC<{ children: ReactNode }> = ({ children }) => {
  const { auth } = useAppConfiguration()

  if (!auth) return <Loader show />

  // Auth0 config delivers a bare domain; Keycloak delivers the full realm URL
  // (https://auth.<env>.tom.hpc-hamburg.de/realms/tom).
  const authority = auth.domain.startsWith('http') ? auth.domain : `https://${auth.domain}`

  return (
    <AuthProvider
      authority={authority}
      client_id={auth.clientId}
      redirect_uri={window.location.origin}
      scope="openid profile email"
      // automaticSilentRenew intentionally OFF: it triggers background
      // signinSilent that flips react-oidc-context isLoading and churned the
      // app tree. Tokens are refreshed on demand by getAccessTokenSilently.
      automaticSilentRenew={false}
      onSigninCallback={onSigninCallback}
      userStore={new WebStorageStateStore({ store: window.localStorage })}
      // Auth0 needs the audience param to issue a JWT access token; Keycloak ignores it.
      {...(auth.audience ? { extraQueryParams: { audience: auth.audience } } : {})}
    >
      {children}
    </AuthProvider>
  )
}

/** @deprecated old Auth0 name — use AuthProviderWithHistory */
export const Auth0ProviderWithHistory = AuthProviderWithHistory
